import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { uploadExternalRequestFile } from "@synthesis/shared-services";
import {
  getCountsQueryKey,
  getRequestQueryKey,
  getRequestsQueryKey,
  useCreateRequest,
  useDeleteAttachmentDescription,
  useSetAttachmentDescription,
  useSubmitRequest,
  useUpdateDraft,
} from "@synthesis/shared-services/external/application/hooks";
import { setPromiseSnack } from "@synthesis/shared-ui";

import { useUserInfo } from "../../../shared/hooks/use-user-info";
import {
  buildCreateApplicationAdditionalData,
  buildCreateApplicationDraftRequestData,
  buildCreateApplicationDraftTitle,
  CREATE_APPLICATION_TYPE_OPTIONS,
  getCreateApplicationDocuments,
  getCreateApplicationRequestType,
  SNACK_TITLES_SAVE_DRAFT,
  SNACK_TITLES_SUBMIT_APPLICATION,
  usePageStore,
  type CreateApplicationDocumentUpload,
} from "../create-application-page.lib";
import { useApplicationDraftId } from "./use-create-application-draft-id";

export const useSaveApplicationDraft = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const userInfo = useUserInfo();
  const pageStore = usePageStore();
  const { draftId, setDraftId } = useApplicationDraftId();
  const createRequestMutation = useCreateRequest<unknown>();
  const updateDraftMutation = useUpdateDraft<unknown>();
  const setAttachmentDescriptionMutation = useSetAttachmentDescription<unknown>();
  const deleteAttachmentDescriptionMutation = useDeleteAttachmentDescription<unknown>();
  const submitRequestMutation = useSubmitRequest<unknown>();
  const [isSaving, setIsSaving] = useState(false);

  const persistDraft = async (): Promise<string> => {
    const selectedCategory = pageStore?.selectedCategory;

    if (!pageStore || !selectedCategory) {
      throw new Error("Не выбран тип заявки");
    }

    const selectedOption = CREATE_APPLICATION_TYPE_OPTIONS.find((option) => option.value === selectedCategory);

    if (!selectedOption) {
      throw new Error("Неизвестный тип заявки");
    }

    const draftTitle = pageStore.getObjectDataProjectName() || buildCreateApplicationDraftTitle(selectedOption.title);

    const draftMeta = {
      category: selectedCategory,
      categoryLabel: selectedOption.title,
    };

    if (!userInfo) {
      throw new Error("Не удалось определить пользователя");
    }

    const requestType = getCreateApplicationRequestType(selectedCategory);
    const documents = getCreateApplicationDocuments(selectedCategory, pageStore.getDocumentsContext());
    const documentUploads = Object.entries(pageStore.documentUploads).flatMap(([documentId, uploads]) =>
      uploads.map((upload) => ({ documentId, upload })),
    ) satisfies Array<{ documentId: string; upload: CreateApplicationDocumentUpload }>;
    const documentsMeta = documents.map((document) => {
      const uploadedFileNames = [
        ...(pageStore.documentServerFiles[document.id] ?? []).map((file) => file.fileName),
        ...(pageStore.documentUploads[document.id] ?? []).map((upload) => upload.fileName),
      ];

      return {
        ...document,
        uploadedFileNames: uploadedFileNames.length > 0 ? uploadedFileNames : undefined,
      };
    });
    const additionalData = buildCreateApplicationAdditionalData({
      ...draftMeta,
      draftTitle,
      applicantData: pageStore.applicantData,
      documents: documentsMeta,
    });
    const draftRequestData = buildCreateApplicationDraftRequestData({
      category: selectedCategory,
      title: draftTitle,
      type: requestType,
      additionalData,
      adjacentObjectData: pageStore.adjacentObjectData,
      crossingObjectData: pageStore.crossingObjectData,
      applicantData: pageStore.applicantData,
    });

    let applicationId = draftId;

    if (!applicationId) {
      const application = await createRequestMutation.mutateAsync({
        body: {
          title: draftRequestData.title ?? draftTitle,
          type: draftRequestData.type ?? requestType,
          additionalData: draftRequestData.additionalData,
          requestSubject: draftRequestData.requestSubject,
          railway: draftRequestData.railway,
          section: draftRequestData.section,
          plannedWorksType: draftRequestData.plannedWorksType,
          adjoiningType: draftRequestData.adjoiningType,
          applicantType: draftRequestData.applicantType,
          applicantName: draftRequestData.applicantName,
          applicantAddress: draftRequestData.applicantAddress,
          applicantEmail: draftRequestData.applicantEmail,
          applicantInn: draftRequestData.applicantInn,
          applicantKpp: draftRequestData.applicantKpp,
          applicantOkpo: draftRequestData.applicantOkpo,
          applicantPhone: draftRequestData.applicantPhone,
        },
      });

      if (!application.id) {
        throw new Error("API не вернул идентификатор заявки");
      }

      applicationId = application.id;
      await setDraftId(applicationId);
    }

    const updatedDraft = await updateDraftMutation.mutateAsync({
      path: { id: applicationId },
      body: draftRequestData,
    });

    queryClient.setQueryData(getRequestQueryKey({ path: { id: applicationId } }), updatedDraft);

    const pendingAttachmentDeletions = pageStore.getPendingAttachmentFileIdsToDelete();

    if (pendingAttachmentDeletions.length > 0) {
      await Promise.all(
        pendingAttachmentDeletions.map((fileId) =>
          deleteAttachmentDescriptionMutation.mutateAsync({
            path: { id: applicationId, fileId },
          }),
        ),
      );
      pageStore.clearPendingAttachmentDeletions(pendingAttachmentDeletions);
    }

    const documentsById = new Map(documents.map((document) => [document.id, document]));

    const committedUploads = await Promise.all(
      documentUploads.map(async ({ documentId, upload }) => {
        const fileMeta = await uploadExternalRequestFile(
          {
            filename: upload.fileName,
            sourceId: applicationId,
            sourceType: "EXTERNAL_REQUEST",
            userId: userInfo.id,
          },
          upload.file,
        );
        const document = documentsById.get(documentId);

        await setAttachmentDescriptionMutation.mutateAsync({
          path: { id: applicationId, fileId: fileMeta.id },
          body: {
            description: `${documentId}: ${document?.title ?? upload.fileName}`,
          },
        });

        return {
          documentId,
          localId: upload.localId,
          fileId: fileMeta.id,
          fileName: upload.fileName,
        };
      }),
    );

    for (const committedUpload of committedUploads) {
      pageStore.commitDocumentUpload(committedUpload.documentId, committedUpload.localId, committedUpload.fileId, committedUpload.fileName);
    }

    await Promise.all([
      queryClient.invalidateQueries({ queryKey: getRequestsQueryKey() }),
      queryClient.invalidateQueries({ queryKey: getCountsQueryKey() }),
    ]);

    pageStore.markDraftSaved();

    return applicationId;
  };

  const runWithSavingState = (task: () => Promise<void>) => {
    if (isSaving) {
      return;
    }

    setIsSaving(true);

    const promise = task().finally(() => {
      setIsSaving(false);
    });

    return promise;
  };

  const saveDraft = () => {
    if (!pageStore?.selectedCategory || isSaving) {
      return;
    }

    const promise = runWithSavingState(async () => {
      await persistDraft();
    });

    if (!promise) {
      return;
    }

    setPromiseSnack({
      promise,
      promiseSettings: {
        title: SNACK_TITLES_SAVE_DRAFT,
      },
    });
  };

  const saveDraftAndLeave = () => {
    if (!pageStore?.selectedCategory || isSaving) {
      return;
    }

    const promise = runWithSavingState(async () => {
      await persistDraft();
      navigate({ to: "/" });
    });

    if (!promise) {
      return;
    }

    setPromiseSnack({
      promise,
      promiseSettings: {
        title: SNACK_TITLES_SAVE_DRAFT,
      },
    });
  };

  const submitApplication = () => {
    if (!pageStore?.selectedCategory || isSaving) {
      return;
    }

    const promise = runWithSavingState(async () => {
      const applicationId = await persistDraft();
      const submitted = await submitRequestMutation.mutateAsync({ path: { id: applicationId } });

      queryClient.setQueryData(getRequestQueryKey({ path: { id: applicationId } }), submitted);

      await Promise.all([
        queryClient.invalidateQueries({ queryKey: getRequestsQueryKey() }),
        queryClient.invalidateQueries({ queryKey: getCountsQueryKey() }),
      ]);

      navigate({ to: "/applications/$id", params: { id: applicationId } });
    });

    if (!promise) {
      return;
    }

    setPromiseSnack({
      promise,
      promiseSettings: {
        title: SNACK_TITLES_SUBMIT_APPLICATION,
      },
    });
  };

  return {
    saveDraft,
    saveDraftAndLeave,
    submitApplication,
    isSaving:
      isSaving ||
      createRequestMutation.isPending ||
      updateDraftMutation.isPending ||
      setAttachmentDescriptionMutation.isPending ||
      deleteAttachmentDescriptionMutation.isPending ||
      submitRequestMutation.isPending,
  };
};
